/**
 * @module WebSocketCompatibility
 * @description Compatibility layer for components still using the old WebSocket manager
 *
 * Provides a minimal interface that matches the old websocketManager API
 * for components that need basic WebSocket connection status.
 *
 * Note: This is a temporary compatibility layer. Components should eventually
 * be updated to use the new simplified WebSocket system directly.
 */

import { webSocketService } from './simpleWebSocket'
import type { DispatchRunnerRoomError, DispatchRunnerRoomJoinPayload } from '@/types/dispatch/events'

interface WebSocketMessage {
  type: string
  data?: unknown
}

type MessageHandler = (message: WebSocketMessage) => void

/**
 * Compatibility WebSocket manager that provides the old API
 */
class WebSocketCompatManager {
  private handlers: Map<string, Set<MessageHandler>> = new Map()
  private eventUnsubscribers: Map<string, () => void> = new Map()
  /** Runner ids whose dispatch:{runner_id} progress room the caller wants joined. */
  private desiredDispatchRunnerRooms: Set<string> = new Set()
  private connectionUnsubscribers: Array<() => void> = []
  private isInitialized = false
  private initPromise: Promise<void> | null = null
  private generation = 0

  /**
   * Initialize the compatibility manager.
   *
   * Idempotent and race-safe: parallel callers that arrive while the underlying
   * `webSocketService.connect()` is still in flight share the same promise
   * instead of each running the post-connect setup. Without this guard every
   * subscriber that arrives during the first connect window would re-run
   * `startConnectionMonitoring` / `attachEventForwarders` and re-log the
   * "initialized" line — visible as 4× duplicate console entries on first load.
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) return
    if (this.initPromise) return this.initPromise

    const generation = this.generation
    this.initPromise = (async () => {
      try {
        await webSocketService.connect()
        if (generation !== this.generation) return
        this.startConnectionMonitoring()
        this.isInitialized = true
        this.attachEventForwarders()
        this.flushRoomSubscriptions()
        this.monitorDispatchRunnerRoomErrors()
        console.log('WebSocket compatibility manager initialized')
      } catch (error) {
        if (generation !== this.generation) return
        console.error(
          'Failed to initialize WebSocket compatibility manager:',
          error
        )
        // Allow a future caller to retry after a transient connect failure.
        this.initPromise = null
        throw error
      }
    })()

    return this.initPromise
  }

  /**
   * Connect (alias for initialize for backward compatibility)
   */
  async connect(): Promise<void> {
    return this.initialize()
  }

  /**
   * Disconnect from WebSocket
   */
  disconnect(): void {
    this.cleanup()
    webSocketService.disconnect()
  }

  /**
   * Check if WebSocket is connected
   */
  isConnected(): boolean {
    return webSocketService.isConnected()
  }

  /**
   * Subscribe to events (compatibility function)
   *
   * @param eventType - Event type to subscribe to
   * @param handler - Message handler function
   * @returns Cleanup function
   */
  subscribe(eventType: string, handler: MessageHandler): () => void {
    if (!this.handlers.has(eventType)) {
      this.handlers.set(eventType, new Set())
    }

    this.handlers.get(eventType)!.add(handler)

    // Initialize connection if not already done
    if (!this.isInitialized) {
      this.initialize()
    } else {
      this.attachEventForwarder(eventType)
    }

    // Return cleanup function
    return () => {
      const handlers = this.handlers.get(eventType)
      if (handlers) {
        handlers.delete(handler)
        if (handlers.size === 0) {
          this.handlers.delete(eventType)
        }
      }
    }
  }

  /**
   * Emit events to subscribers
   */
  private emit(eventType: string, data?: unknown): void {
    const handlers = this.handlers.get(eventType)
    if (handlers) {
      const message: WebSocketMessage = {
        type: eventType,
        data,
      }

      handlers.forEach((handler) => {
        try {
          handler(message)
        } catch (error) {
          console.error(
            `Error in WebSocket event handler for ${eventType}:`,
            error
          )
        }
      })
    }
  }

  /**
   * Forward Socket.IO events from the simplified service into old-style subscribers.
   */
  private attachEventForwarder(eventType: string): void {
    if (eventType === 'connection' || this.eventUnsubscribers.has(eventType)) {
      return
    }

    const unsubscribe = webSocketService.on(eventType, (data: unknown) => {
      this.emit(eventType, data)
    })

    this.eventUnsubscribers.set(eventType, unsubscribe)
  }

  private attachEventForwarders(): void {
    for (const eventType of this.handlers.keys()) {
      this.attachEventForwarder(eventType)
    }
  }

  /** Forward canonical transport status and restore this facade's desired rooms. */
  private startConnectionMonitoring(): void {
    for (const unsubscribe of this.connectionUnsubscribers) unsubscribe()
    this.connectionUnsubscribers = [
      webSocketService.subscribeToConnectionStatus((connected) => {
        this.emit('connection', { connected })
      }),
      webSocketService.subscribeToReconnect(() => this.flushRoomSubscriptions()),
    ]
  }

  /**
   * Cleanup resources
   */
  cleanup(): void {
    this.generation += 1
    for (const unsubscribe of this.connectionUnsubscribers) {
      unsubscribe()
    }
    this.connectionUnsubscribers = []
    for (const unsubscribe of this.eventUnsubscribers.values()) {
      unsubscribe()
    }
    this.eventUnsubscribers.clear()
    this.handlers.clear()
    this.desiredDispatchRunnerRooms.clear()
    this.isInitialized = false
    this.initPromise = null
  }

  /**
   * Join the authenticated dispatch:{runner_id} progress room. The server
   * owns the room name and constructs it from runner_id — callers, and this
   * layer, never send a raw room string, only the typed join payload.
   */
  joinDispatchRunnerRoom(runnerId: string): void {
    this.desiredDispatchRunnerRooms.add(runnerId)

    if (!this.isInitialized) {
      this.initialize()
      return
    }

    this.emitDispatchRunnerRoomJoin(runnerId)
  }

  /**
   * Leave a previously joined dispatch runner room. Removes it from the
   * reconnect-replay set first, so a reconnect racing this call never
   * rejoins a room the caller just left.
   */
  leaveDispatchRunnerRoom(runnerId: string): void {
    this.desiredDispatchRunnerRooms.delete(runnerId)
    if (this.isInitialized) {
      const payload: DispatchRunnerRoomJoinPayload = { runner_id: runnerId }
      webSocketService.emit('dispatch_runner_room:leave', payload)
    }
  }

  private emitDispatchRunnerRoomJoin(runnerId: string): void {
    const payload: DispatchRunnerRoomJoinPayload = { runner_id: runnerId }
    webSocketService.emit('dispatch_runner_room:join', payload)
  }

  /**
   * Log every sanitized dispatch_runner_room:error the server sends, in
   * addition to the normal forwarding subscribe() already provides to any
   * caller listening for it — a join denial should never be silently lost
   * just because no UI happens to be subscribed at the time.
   */
  private monitorDispatchRunnerRoomErrors(): void {
    this.subscribe('dispatch_runner_room:error', (message) => {
      const err = message.data as DispatchRunnerRoomError | undefined
      console.warn('Dispatch runner room request denied:', err)
    })
  }

  /**
   * Emit an event through the underlying Socket.IO service.
   */
  emitToServer(event: string, data: unknown): void {
    if (!this.isInitialized) {
      this.initialize()
        .then(() => webSocketService.emit(event, data))
        .catch(console.error)
      return
    }

    webSocketService.emit(event, data)
  }

  private flushRoomSubscriptions(): void {
    for (const runnerId of this.desiredDispatchRunnerRooms) {
      this.emitDispatchRunnerRoomJoin(runnerId)
    }
  }

  /**
   * Returns true when the last connect attempt was refused with authz_denied.
   * A policy-level refusal means the user is authenticated but lacks the
   * required role — reconnection is suppressed until the role is granted.
   */
  isAuthzDenied(): boolean {
    return webSocketService.isAuthzDenied()
  }

  /**
   * Register a callback invoked once when an authz_denied refusal is received.
   * Fires immediately if authz_denied was already raised before this call.
   * Returns a cleanup function.
   */
  onAuthzDenied(cb: () => void): () => void {
    return webSocketService.onAuthzDenied(cb)
  }

  /**
   * Get connection statistics
   */
  getStats(): unknown {
    return webSocketService.getConnectionStats()
  }
}

// Export singleton instance for compatibility
export const websocketManager = new WebSocketCompatManager()
